import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';

type RestoreDeletedAddressResponse = {
  restoreDeletedAddress: {
    addressId: string;
  };
};

const RESTORE_ADDRESS_MUTATION = gql`
  mutation RestoreDeletedAddress($params: RestoreDeletedAddressInput!) {
    restoreDeletedAddress(params: $params) {
      addressId
    }
  }
`;

export const useRestoreAddress = () => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { groupRules } = useGroupRules();
  const [restoreAddressMutation] = useMutation<RestoreDeletedAddressResponse>(
    RESTORE_ADDRESS_MUTATION,
    {
      update: (cache, result) => {
        if (!result.data?.restoreDeletedAddress?.addressId) {
          return;
        }
        const id = cache.identify({
          __typename: 'Address',
          id: result.data.restoreDeletedAddress.addressId,
        });
        cache.modify({
          id,
          fields: generateFieldModifiersMap({
            deletedAt: null,
            __typename: 'Address',
          }),
        });
      },
      onCompleted: () => {
        showSuccessAlert('Site was restored');
      },
      onError: () => {
        showErrorAlert(`Could not restore site`);
      },
    },
  );

  const restoreAddress = useCallback(
    async (addressId: string) => {
      if (groupRules[UserGroupRules.AccessToRestoreDeletedAddresses]) {
        await restoreAddressMutation({
          variables: {
            params: {
              addressId,
            },
          },
        });
      }
    },
    [groupRules, restoreAddressMutation],
  );

  return {
    restoreAddress,
  };
};
